import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import { FormControl, InputAdornment, IconButton, styled } from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';

const OptionContainer = styled('div')(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  width: '100%',
  '& > span': {
    fontSize: '0.875rem',
  },
}));

const HintText = styled('span')(({ theme }) => ({
  color: theme.palette.grey[500],
}));

const CustomAutocomplete = styled(Autocomplete)(({ theme, disabled }) => ({
  ...(disabled && {
    '& .MuiInputBase-root': {
      backgroundColor: theme.palette.action.disabledBackground,
    },
    '& .MuiInputBase-input': {
      color: theme.palette.text.disabled,
    },
  }),
}));

const DropdownFieldComponent = ({
  label,
  options,
  value,
  onChange,
  hint,
  helperText,
  required,
  submitted,
  includeOther = false,
  disabled = false
}) => {
  const [isOtherSelected, setIsOtherSelected] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    if (isOtherSelected && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOtherSelected]);

  const handleChange = (_, newValue) => {
    if (newValue?.label === 'Other') {
      setIsOtherSelected(true);
    } else {
      onChange(newValue || null);
      setIsOtherSelected(false);
    }
  };

  const handleOtherChange = (event) => {
    onChange({ label: event.target.value });
  };

  const handleBlur = () => {
    if (value?.label === '') {
      setIsOtherSelected(false);
    }
  };

  const handleClear = () => {
    onChange(null);
    setIsOtherSelected(false);
  };

  const isValidValue = options.some((option) => option.label === value?.label) || (includeOther && value?.label === 'Other');
  const showError = submitted && required && !isValidValue;
  const formattedLabel = required ? `${label} *` : label;

  const dropdownOptions = includeOther ? [...options, { label: 'Other' }] : options;

  return (
    <FormControl fullWidth required={required} error={showError}>
      {isOtherSelected ? (
        <TextField
          label={formattedLabel}
          placeholder="Type your answer..."
          value={value?.label}
          onChange={handleOtherChange}
          onBlur={handleBlur}
          helperText={showError ? 'This field is required' : helperText}
          error={showError}
          fullWidth
          inputRef={inputRef}
          variant="outlined"
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                {isOtherSelected && (
                  <IconButton edge="end" onClick={handleClear} size="small">
                    <ClearIcon />
                  </IconButton>
                )}
              </InputAdornment>
            ),
          }}
          disabled={disabled}
        />
      ) : (
        <CustomAutocomplete
          options={dropdownOptions}
          value={dropdownOptions.find((option) => option.label === value?.label) || null}
          onChange={handleChange}
          renderInput={(params) => (
            <TextField
              {...params}
              label={formattedLabel}
              placeholder={hint || ''}
              helperText={showError ? 'This field is required' : helperText}
              error={showError}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {value?.label && (
                      <IconButton edge="end" onClick={handleClear} size="small">
                        <ClearIcon />
                      </IconButton>
                    )}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
              disabled={disabled}
            />
          )}
          renderOption={(props, option) => (
            <li {...props}>
              <OptionContainer>
                <span>{option.label}</span>
                {option.hint && <HintText>{option.hint}</HintText>}
              </OptionContainer>
            </li>
          )}
          disableClearable
          autoHighlight
          autoSelect
          fullWidth
          isOptionEqualToValue={(option, val) => option.label === val?.label}
          disabled={disabled}
        />
      )}
    </FormControl>
  );
};

DropdownFieldComponent.propTypes = {
  label: PropTypes.string.isRequired,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      hint: PropTypes.string,
    })
  ).isRequired,
  value: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      hint: PropTypes.string,
    }),
  ]),
  onChange: PropTypes.func.isRequired,
  hint: PropTypes.string,
  helperText: PropTypes.string,
  required: PropTypes.bool,
  submitted: PropTypes.bool,
  includeOther: PropTypes.bool,
  disabled: PropTypes.bool,
};

export default DropdownFieldComponent;
